import React from 'react'
import type { ReactElement, ReactNode } from 'react'
import { useEffect, useRef } from 'react'

import type { ICourseLabelProps, ICourseOption } from './CourseKitTypes'

interface IOptionLabelProps<V> extends ICourseLabelProps {
  option?: ICourseOption<V>
}

export const CourseOptionLabel = <V,>({ option, ...rest }: IOptionLabelProps<V>) => {
  if (typeof option?.label === 'function') {
    const Label = option.label
    return <Label {...option} {...rest} />
  }
  return option?.label ?? option?.value
}

export function getTextFromReactElement(element: ReactElement): string {
  function extract(node: ReactNode): string {
    if (typeof node === 'string' || typeof node === 'number') {
      return node.toString()
    }
    if (Array.isArray(node)) {
      return node.map(extract).join('')
    }
    if (node && typeof node === 'object' && 'props' in node) {
      return extract((node as unknown as { props: { children: ReactNode } }).props.children)
    }
    return ''
  }

  return extract(element.props.children)
}

export function buildTestAnnouncement(
  isValid: boolean,
  correctAnswer?: string,
  yourAnswer?: string,
): string {
  const parts = [isValid ? 'Correct.' : 'Incorrect.']
  if (correctAnswer) {
    parts.push(`Correct answer: ${correctAnswer}.`)
  }
  if (yourAnswer) {
    parts.push(`Your answer: ${yourAnswer}.`)
  }
  return parts.join(' ')
}

/**
 * useLiveFeedback
 * ANNOUNCE text to screen readers using aria-live region.
 */
export function useLiveFeedback(live: 'polite' | 'assertive' = 'polite') {
  const regionRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const region = document.createElement('div')
    region.setAttribute('aria-live', live)
    region.setAttribute('aria-atomic', 'true')
    region.style.position = 'absolute'
    region.style.left = '-9999px'
    region.style.top = 'auto'
    region.style.width = '1px'
    region.style.height = '1px'
    region.style.overflow = 'hidden'

    document.body.appendChild(region)
    regionRef.current = region

    return () => {
      region.remove()
    }
  }, [live])

  /**
   * Make screen reader read a new message.
   */
  const announce = (message: string) => {
    if (!regionRef.current) {
      return
    }

    regionRef.current.textContent = ''
    setTimeout(() => {
      regionRef.current!.textContent = message
    }, 10)
  }

  return announce
}
